import { useMutation, useQuery } from 'convex/react'
import { useState } from 'react'
import { api } from '@backend/_generated/api'
import type { Id } from '@backend/_generated/dataModel'

export type Holiday = { date: string; name?: string }

export type PeriodConfigSnapshot = {
  configured: boolean
  monthsCount?: number
  startDate?: number
  holidays: Array<Holiday>
  country?: string
  canEdit: boolean
}

export type SupportedCountry = {
  code: string
  name: string
}

/**
 * Live snapshot of an org's analytics period configuration plus a flag
 * indicating whether the current user may edit it.
 */
export function usePeriodConfig(organizationId: string | undefined) {
  const result = useQuery(
    api.organizationConfig.getPeriodConfig,
    organizationId
      ? { organizationId: organizationId as Id<'organizations'> }
      : 'skip',
  )

  return {
    data: result as PeriodConfigSnapshot | undefined,
    isLoading: result === undefined,
  }
}

export type CurrentPeriod = {
  _id: string
  startsAt: number
  endsAt: number
}

/**
 * The org's currently active assessment period, or null if none exists yet.
 * Drives the "Assess all direct reports" / "Create assessment" availability
 * guards.
 */
export function useCurrentPeriod(organizationId: string | undefined) {
  const result = useQuery(
    api.organizationConfig.getCurrentPeriod,
    organizationId
      ? { organizationId: organizationId as Id<'organizations'> }
      : 'skip',
  )

  return {
    data: result as CurrentPeriod | null | undefined,
    isLoading: result === undefined,
  }
}

export type PeriodReadiness =
  | { status: 'loading' }
  | { status: 'not-configured'; canEdit: boolean }
  | { status: 'awaiting-start'; startDate: number; canEdit: boolean }
  | { status: 'no-current-period'; canEdit: boolean }
  | { status: 'ready'; period: CurrentPeriod }

/**
 * Single source of truth for "can the user start an assessment right now?".
 * Combines the period configuration and the currently active period into a
 * discriminated state so callers can render the right CTA / banner.
 */
export function usePeriodReadiness(
  organizationId: string | undefined,
): PeriodReadiness {
  const { data: config, isLoading: isConfigLoading } =
    usePeriodConfig(organizationId)
  const { data: period, isLoading: isPeriodLoading } =
    useCurrentPeriod(organizationId)

  if (isConfigLoading || isPeriodLoading || !config) {
    return { status: 'loading' }
  }

  if (!config.configured) {
    return { status: 'not-configured', canEdit: config.canEdit }
  }

  if (period) {
    return { status: 'ready', period }
  }

  const now = Date.now()
  if (config.startDate !== undefined && config.startDate > now) {
    return {
      status: 'awaiting-start',
      startDate: config.startDate,
      canEdit: config.canEdit,
    }
  }

  return { status: 'no-current-period', canEdit: config.canEdit }
}

/**
 * Full ISO 3166-1 alpha-2 list of countries that `date-holidays` ships
 * holiday data for. Used to populate the country selector.
 */
export function useSupportedCountries() {
  const result = useQuery(api.organizationConfig.listSupportedCountries, {})
  return {
    data: (result ?? []) as Array<SupportedCountry>,
    isLoading: result === undefined,
  }
}

export type PublicHoliday = { date: string; name: string }

/**
 * Public holidays known to `date-holidays` for the given country, covering
 * the current and next calendar year. Used to show users what dates are
 * already auto-skipped before they add custom ones.
 */
export function usePublicHolidaysForCountry(country: string | undefined) {
  const result = useQuery(
    api.organizationConfig.listPublicHolidaysForCountry,
    country ? { country } : 'skip',
  )
  return {
    data: (result ?? []) as Array<PublicHoliday>,
    isLoading: country !== undefined && result === undefined,
  }
}

export type UpdatePeriodConfigInput = {
  monthsCount?: number
  startDate?: number
  holidays?: Array<Holiday>
  /** Empty string clears the country. */
  country?: string | null
}

/**
 * Patch a subset of an org's period configuration. Server validates
 * permissions (admin or analytics role) and field shapes.
 */
export function useUpdatePeriodConfig(organizationId: string | undefined) {
  const fn = useMutation(api.organizationConfig.updatePeriodConfig)
  const [isPending, setPending] = useState(false)

  const mutateAsync = async (input: UpdatePeriodConfigInput) => {
    if (!organizationId) throw new Error('Organization required')
    setPending(true)
    try {
      return await fn({
        organizationId: organizationId as Id<'organizations'>,
        ...input,
      })
    } finally {
      setPending(false)
    }
  }

  return { isPending, mutateAsync }
}

/**
 * Admin-only escape hatch — deletes future periods whose initial assessment
 * batch has not yet been emailed so that startDate / monthsCount changes
 * take effect on the next cron run.
 */
export function useResetUpcomingPeriods(organizationId: string | undefined) {
  const fn = useMutation(api.organizationConfig.resetUpcomingPeriods)
  const [isPending, setPending] = useState(false)

  const mutateAsync = async () => {
    if (!organizationId) throw new Error('Organization required')
    setPending(true)
    try {
      return await fn({
        organizationId: organizationId as Id<'organizations'>,
      })
    } finally {
      setPending(false)
    }
  }

  return { isPending, mutateAsync }
}

/**
 * Platform-admin only. Triggers `populatePeriodForOrg` immediately rather
 * than waiting for the daily 06:00 UTC cron.
 */
export function useRunPopulatePeriodsNow(organizationId: string | undefined) {
  const fn = useMutation(api.organizationConfig.runPopulatePeriodsNow)
  const [isPending, setPending] = useState(false)

  const mutateAsync = async () => {
    if (!organizationId) throw new Error('Organization required')
    setPending(true)
    try {
      return await fn({
        organizationId: organizationId as Id<'organizations'>,
      })
    } finally {
      setPending(false)
    }
  }

  return { isPending, mutateAsync }
}
